import {
  DECISION_LABEL,
  RMIT_AREAS,
  packPeriod,
  type DecisionOutcome,
  type EvidencePack,
  type RmitControl,
} from '@cyberscore/contracts';
import { AlertTriangle, Download, FileCheck2, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '../../ui/Toasts';
import { saveAuditTrail, savePack, useEvidencePack, type Period } from './api';

const STATUS: Record<RmitControl['status'], { label: string; tone: string }> = {
  in_place: { label: 'In place', tone: 'ok' },
  partial: { label: 'Partly', tone: 'warn' },
  planned: { label: 'Planned', tone: 'neutral' },
};
const OWNER: Record<RmitControl['owner'], string> = {
  cyberscore: 'CyberScore',
  insurer: 'You',
  shared: 'Shared',
};
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kuala_Lumpur' }).format(new Date());
const when = (iso: string) => new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }); // prettier-ignore

/**
 * The RMiT evidence pack (task 50): how CyberScore meets Bank Negara Malaysia's Risk Management in Technology
 * policy as an outsourced service, with this tenant's own records for a period. Building one is recorded
 * in the audit trail.
 */
export function EvidencePage() {
  const [period, setPeriod] = useState<Period>(() => packPeriod({}, today()));
  const [asked, setAsked] = useState<Period | null>(null);
  const pack = useEvidencePack(asked);
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  const csv = async () => {
    setSaving(true);
    try {
      await saveAuditTrail(asked ?? period);
    } catch (e) {
      toast((e as Error).message, 'bad');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <h1>RMiT evidence pack</h1>
        <p>
          For your outsourcing register and auditors: how CyberScore meets Bank Negara Malaysia’s Risk Management in Technology policy, with your own records for the period. Building a pack or exporting the audit trail is itself recorded.
        </p>
      </div>

      <section className="card pad">
        <div className="row gap wrap evidence-period">
          <div className="field">
            <label htmlFor="ev-from">From</label>
            <input id="ev-from" type="date" className="input" value={period.from} max={period.to} onChange={(e) => setPeriod({ ...period, from: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="ev-to">To</label>
            <input id="ev-to" type="date" className="input" value={period.to} min={period.from} max={today()} onChange={(e) => setPeriod({ ...period, to: e.target.value })} />
          </div>
          <button type="button" className="btn btn-primary" disabled={pack.isFetching || !period.from || !period.to} onClick={() => setAsked({ ...period })}>
            <FileCheck2 size={16} /> {pack.isFetching ? 'Building…' : 'Build the pack'}
          </button>
          <button type="button" className="btn" disabled={saving} onClick={csv}>
            <Download size={16} /> Audit trail (CSV)
          </button>
        </div>
        <p className="muted small mt">Up to 366 days. Dates are Malaysia time.</p>
        {pack.isError && <div className="error mt" role="alert">{pack.error.message}</div>}
      </section>

      {pack.data && <Pack pack={pack.data} />}
    </>
  ); // prettier-ignore
}

function Pack({ pack }: { pack: EvidencePack }) {
  const c = pack.coverage;
  return (
    <>
      <section className="card pad mt" aria-labelledby="ev-summary">
        <div className="row-between wrap gap">
          <div>
            <h2 id="ev-summary" className="section-title">
              {pack.tenant.name} · {when(pack.period.from)} to {when(pack.period.to)}
            </h2>
            <p className="muted small">
              Built {new Date(pack.generated_at).toLocaleString('en-MY')} by {pack.generated_by}. {c.in_place} of {c.total} controls in place, {c.partial} partly, {c.planned} planned.
            </p>
          </div>
          <button type="button" className="btn btn-primary" onClick={() => savePack(pack)}>
            <Download size={16} /> Download the pack (JSON)
          </button>
        </div>
        <div className="small mt">
          Digest (SHA-256): <span className="mono break">{pack.digest}</span>
        </div>
      </section>

      <section className="card mt" aria-labelledby="ev-controls">
        <div className="card-head">
          <h2 id="ev-controls" className="card-title row gap">
            <ShieldCheck size={16} /> Controls by RMiT area
          </h2>
        </div>
        <div className="table-wrap">
          <table className="grid evidence-controls">
            <thead>
              <tr>
                <th>Area</th>
                <th>Requirement and how it is met</th>
                <th>Status</th>
                <th>Who</th>
              </tr>
            </thead>
            <tbody>
              {RMIT_AREAS.flatMap((area) =>
                pack.controls.filter((x) => x.area === area).map((x, i) => (
                  <tr key={x.id}>
                    <td className="small">{i === 0 ? area : ''}</td>
                    <td>
                      <div><span className="mono small">{x.id}</span> {x.requirement}</div>
                      <div className="muted small">{x.how}</div>
                      {x.gap && <div className="small warn-t">{x.gap}</div>}
                      {x.evidence.length > 0 && <div className="muted small">Evidence: {x.evidence.map((e) => (e.kind === 'pack' ? `this pack (${e.ref.replace('_', ' ')})` : e.ref)).join(' · ')}</div>}
                    </td>
                    <td><span className={`pill ${STATUS[x.status].tone}`}>{STATUS[x.status].label}</span></td>
                    <td className="small">{OWNER[x.owner]}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card mt" aria-labelledby="ev-access">
        <div className="card-head">
          <h2 id="ev-access" className="card-title">Access review · {pack.access.members.length} people</h2>
        </div>
        {pack.access.findings.length > 0 ? (
          <ul className="problem-list pad-x">
            {pack.access.findings.map((f, i) => (
              <li key={i}><AlertTriangle size={14} className="warn-t" /> {f.message}</li>
            ))}
          </ul>
        ) : (
          <p className="muted small pad-x">Nothing to follow up: no dormant accounts or risky role combinations.</p>
        )}
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr><th>Person</th><th>Roles</th><th>Status</th><th>Last signed in</th></tr>
            </thead>
            <tbody>
              {pack.access.members.map((m) => (
                <tr key={m.user_id}>
                  <td>{m.email ?? m.name ?? m.user_id}</td>
                  <td className="small">{m.roles.join(', ').replace(/_/g, ' ')}</td>
                  <td><span className={`pill ${m.status === 'active' ? 'ok' : 'neutral'}`}>{m.status}</span></td>
                  <td className="muted">{when(m.last_seen_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card mt" aria-labelledby="ev-changes">
        <div className="card-head">
          <h2 id="ev-changes" className="card-title">Configuration changes · {pack.changes.length}</h2>
          <span className="muted small">Each needs a second person</span>
        </div>
        {pack.changes.length ? (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr><th>Change</th><th>Asked by</th><th>Decided by</th><th>Status</th></tr>
              </thead>
              <tbody>
                {pack.changes.map((r) => (
                  <tr key={r.id}>
                    <td>{r.note}<div className="muted small">{when(r.submitted_at)}{r.kind === 'rollback' ? ' · rollback' : ''}</div></td>
                    <td className="small">{r.submitted_by}</td>
                    <td className="small">{r.decided_by ?? '—'}{r.segregated === false && <span className="pill bad ml">same person</span>}</td>
                    <td><span className={`pill ${r.status === 'approved' ? 'ok' : r.status === 'rejected' ? 'bad' : 'neutral'}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted small pad-x">No configuration changes in this period.</p>
        )}
      </section>

      <section className="card mt" aria-labelledby="ev-rescores">
        <div className="card-head">
          <h2 id="ev-rescores" className="card-title">Re-scores · {pack.rescores.length}</h2>
          <span className="muted small">Each needs two approvers besides the requester</span>
        </div>
        {pack.rescores.length ? (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr><th>Re-score</th><th>Asked by</th><th>Approved by</th><th>Status</th></tr>
              </thead>
              <tbody>
                {pack.rescores.map((r) => (
                  <tr key={r.id}>
                    <td>{r.note}<div className="muted small">{when(r.requested_at)} · {r.assessments} assessments</div></td>
                    <td className="small">{r.requested_by}</td>
                    <td className="small">{r.approvers.join(', ') || '—'}</td>
                    <td><span className={`pill ${r.status === 'applied' ? 'ok' : r.status === 'rejected' ? 'bad' : 'neutral'}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted small pad-x">No re-scores in this period.</p>
        )}
      </section>

      <section className="card mt" aria-labelledby="ev-decisions">
        <div className="card-head">
          <h2 id="ev-decisions" className="card-title">Underwriting decisions · {pack.decisions.length}</h2>
          <span className="muted small">Each on the score it was made on; conditions as they stand now</span>
        </div>
        {pack.decisions.length ? (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr><th>Business</th><th>Decision</th><th>By</th><th className="num">Score</th><th>Conditions</th></tr>
              </thead>
              <tbody>
                {pack.decisions.map((d) => (
                  <tr key={d.id}>
                    <td>{d.business}<div className="muted small">{when(d.decided_at)}</div></td>
                    <td>{DECISION_LABEL[d.outcome as DecisionOutcome] ?? d.outcome}</td>
                    <td className="small">{d.decided_by}</td>
                    <td className="num">{d.score ?? '—'}</td>
                    <td className="small">{d.conditions ? `${d.conditions_met} of ${d.conditions} met${d.conditions_open ? `, ${d.conditions_open} open` : ''}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted small pad-x">No underwriting decisions in this period.</p>
        )}
      </section>

      <section className="card mt" aria-labelledby="ev-ai">
        <div className="card-head">
          <h2 id="ev-ai" className="card-title">AI processors that handled your data</h2>
        </div>
        {pack.ai_processors.length ? (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr><th>Provider</th><th>Model</th><th>Used for</th><th className="num">Calls</th><th>Between</th></tr>
              </thead>
              <tbody>
                {pack.ai_processors.map((p) => (
                  <tr key={`${p.provider}/${p.model}`}>
                    <td>{p.provider}</td>
                    <td className="mono small">{p.model ?? '—'}</td>
                    <td className="small">{p.agents.join(', ')}</td>
                    <td className="num">{p.calls.toLocaleString('en-MY')}</td>
                    <td className="muted small">{when(p.first_call)} – {when(p.last_call)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted small pad-x">No AI calls in this period.</p>
        )}
      </section>

      <section className="card pad mt" aria-labelledby="ev-audit">
        <h2 id="ev-audit" className="section-title">Audit trail</h2>
        <p className="small">
          {pack.audit_trail.events.toLocaleString('en-MY')} events in the period. The trail is append-only: nobody, including CyberScore’s own database role, can change or delete an event. Export it as CSV above.
        </p>
        <ul className="evidence-types small muted">
          {Object.entries(pack.audit_trail.by_type).sort((x, y) => y[1] - x[1]).slice(0, 12).map(([t, n]) => (
            <li key={t}><span className="mono">{t}</span> {n.toLocaleString('en-MY')}</li>
          ))}
        </ul>
      </section>
    </>
  ); // prettier-ignore
}
